/**
 * @fileoverview Пресеты v10.0.0 — все 20 из v9.1.0 плюс поля новых эффектов
 * @module MikhmelViewer/Cinematic10/Presets
 *
 * Совместимость: структура и идентификаторы пресетов v9.1.0 сохранены
 * без изменений, добавлены только новые ключи (volumetric, ssgi,
 * motionBlur, sss, denoise, upscale). Старый код, читающий preset.ssao
 * или preset.bloom, продолжит работать как прежде.
 *
 * Новые эффекты по умолчанию выключены везде, кроме пресетов, где они
 * несут смысл — включать объёмный свет в «Экстренном» режиме, задача
 * которого показать снимок максимально быстро и читаемо, было бы вредно.
 */

const F = Object.freeze;

/** Дефолты новых эффектов v9.2/v10 — подмешиваются в каждый пресет. */
const V10_DEFAULTS = F({
  volumetric: F({ enabled: false, intensity: 0.5, scattering: 0.3, absorption: 0.05,
                  anisotropy: 0.6, steps: 32, shadows: true, maxDistance: 40 }),
  ssgi: F({ enabled: false, intensity: 0.4, radius: 24, bounces: 2,
            thickness: 0.06, samples: 16, temporal: true }),
  motionBlur: F({ enabled: false, intensity: 0.3, samples: 12, shutterAngle: 0.5 }),
  sss: F({ enabled: false, strength: 0.5, radius: 1.2,
           falloffR: 1.0, falloffG: 3.5, falloffB: 7.0,
           thicknessScale: 2.0, samples: 12 }),
  denoise: F({ enabled: true, preset: 'balanced', strength: 0.7 }),
  upscale: F({ enabled: false, mode: 'fsr', quality: 'quality', sharpness: 0.25 }),
  ssr: F({ enabled: false, intensity: 0.3, roughness: 0.5, maxDist: 30 }),
  fog: F({ enabled: false, density: 0.15, falloff: 2.0, height: 0.3,
           color: F([0.05, 0.08, 0.12]) }),
  lut: F({ mode: 0, intensity: 0.5 }),
  bokeh: F({ enabled: false, size: 0.5, bright: 2.0, rings: 4 }),
});

/** Слить дефолты новых эффектов с телом пресета, не перетирая заданное явно. */
const withV10 = (preset, overrides = {}) => F({
  ...V10_DEFAULTS,
  ...preset,
  ...overrides,
});

// ═══════════════════════════════════════════════════════════════════
// ПРЕСЕТЫ
// ═══════════════════════════════════════════════════════════════════

const _PRESETS = {
  off: withV10({
    name: 'Выкл', description: 'Без обработки',
    ssao: F({ enabled: false }), bloom: F({ enabled: false }),
    godRays: F({ enabled: false }), fxaa: F({ enabled: false }),
    sharpen: 0, vignette: F({ intensity: 0 }),
    toneMap: F({ mode: 0, exposure: 1, gamma: 1 }),
    colorGrade: F({ saturation: 1, contrast: 1, tint: F([1, 1, 1]), temperature: 0 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0, color: F([0.3, 0.6, 1]) }),
    chromAb: 0, grain: 0,
  }, {
    denoise: F({ enabled: false, preset: 'off', strength: 0 }),
  }),

  cinematic: withV10({
    name: 'Cinematic', description: 'Фотореалистичный',
    ssao: F({ enabled: true, radius: 8, intensity: 0.6, bias: 0.01, samples: 24 }),
    bloom: F({ enabled: true, threshold: 0.65, softKnee: 0.3, intensity: 0.25, passes: 5 }),
    godRays: F({ enabled: true, intensity: 0.3, density: 0.8, decay: 0.95, weight: 0.3 }),
    fxaa: F({ enabled: true }), sharpen: 0.4,
    vignette: F({ intensity: 0.35, radius: 0.85 }),
    toneMap: F({ mode: 1, exposure: 1.3, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.1, contrast: 1.05, tint: F([1, 0.98, 0.95]), temperature: 0.2 }),
    dof: F({ intensity: 0.3, focus: 0.5, range: 0.35 }),
    edge: F({ intensity: 0.15, color: F([0.4, 0.7, 1]) }),
    chromAb: 0.15, grain: 0.2,
  }, {
    // Флагманский пресет — здесь новые эффекты включены полностью
    volumetric: F({ enabled: true, intensity: 0.5, scattering: 0.3, absorption: 0.05,
                    anisotropy: 0.65, steps: 40, shadows: true, maxDistance: 40 }),
    ssgi: F({ enabled: true, intensity: 0.4, radius: 24, bounces: 2,
              thickness: 0.06, samples: 16, temporal: true }),
    motionBlur: F({ enabled: true, intensity: 0.3, samples: 12, shutterAngle: 0.5 }),
    bokeh: F({ enabled: true, size: 0.5, bright: 2.0, rings: 4 }),
  }),

  surgical: withV10({
    name: 'Surgical', description: 'Хирургический',
    ssao: F({ enabled: true, radius: 4, intensity: 0.3, bias: 0.02, samples: 16 }),
    bloom: F({ enabled: false }), godRays: F({ enabled: false }),
    fxaa: F({ enabled: true }), sharpen: 0.7, vignette: F({ intensity: 0 }),
    toneMap: F({ mode: 0, exposure: 1.4, gamma: 2.0 }),
    colorGrade: F({ saturation: 0.9, contrast: 1.1, tint: F([1, 1, 1]), temperature: 0 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.1, color: F([1, 1, 1]) }),
    chromAb: 0, grain: 0,
  }, {
    // Хирургу нужна геометрическая достоверность: никаких размытий и
    // додуманного света. Оставлен только денойз — он ничего не выдумывает.
    denoise: F({ enabled: true, preset: 'light', strength: 0.4 }),
  }),

  xray: withV10({
    name: 'X-Ray Glow', description: 'Рентгеновское свечение',
    ssao: F({ enabled: false }),
    bloom: F({ enabled: true, threshold: 0.3, softKnee: 0.5, intensity: 0.5, passes: 6 }),
    godRays: F({ enabled: true, intensity: 0.6, density: 0.9, decay: 0.97, weight: 0.4 }),
    fxaa: F({ enabled: true }), sharpen: 0.2,
    vignette: F({ intensity: 0.5, radius: 0.75 }),
    toneMap: F({ mode: 2, exposure: 1.5, gamma: 2.4 }),
    colorGrade: F({ saturation: 0.6, contrast: 1.2, tint: F([0.85, 0.95, 1]), temperature: -0.5 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.4, color: F([0.3, 0.5, 1]) }),
    chromAb: 0.2, grain: 0.1,
  }, {
    volumetric: F({ enabled: true, intensity: 0.7, scattering: 0.45, absorption: 0.03,
                    anisotropy: 0.4, steps: 48, shadows: false, maxDistance: 50 }),
  }),

  anatomical: withV10({
    name: 'Anatomical', description: 'Тёплый анатомический',
    ssao: F({ enabled: true, radius: 12, intensity: 0.5, bias: 0.01, samples: 20 }),
    bloom: F({ enabled: true, threshold: 0.75, softKnee: 0.2, intensity: 0.15, passes: 4 }),
    godRays: F({ enabled: true, intensity: 0.2, density: 0.75, decay: 0.94, weight: 0.25 }),
    fxaa: F({ enabled: true }), sharpen: 0.3,
    vignette: F({ intensity: 0.25, radius: 0.9 }),
    toneMap: F({ mode: 3, exposure: 1.2, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.15, contrast: 1.0, tint: F([1, 0.95, 0.88]), temperature: 0.5 }),
    dof: F({ intensity: 0.2, focus: 0.45, range: 0.4 }),
    edge: F({ intensity: 0.08, color: F([1, 0.8, 0.5]) }),
    chromAb: 0.05, grain: 0.15,
  }, {
    // Единственный пресет с SSS: подповерхностное рассеяние — это про
    // биологические ткани, а анатомический режим ровно про них
    sss: F({ enabled: true, strength: 0.55, radius: 1.4,
             falloffR: 1.0, falloffG: 3.5, falloffB: 7.0,
             thicknessScale: 2.0, samples: 14 }),
    ssgi: F({ enabled: true, intensity: 0.45, radius: 28, bounces: 2,
              thickness: 0.07, samples: 16, temporal: true }),
  }),

  highContrast: withV10({
    name: 'High Contrast', description: 'Для презентаций',
    ssao: F({ enabled: true, radius: 6, intensity: 0.8, bias: 0.01, samples: 16 }),
    bloom: F({ enabled: true, threshold: 0.5, softKnee: 0.3, intensity: 0.2, passes: 4 }),
    godRays: F({ enabled: false }), fxaa: F({ enabled: true }), sharpen: 0.8,
    vignette: F({ intensity: 0.4, radius: 0.8 }),
    toneMap: F({ mode: 1, exposure: 1.5, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.2, contrast: 1.3, tint: F([1, 1, 1]), temperature: 0 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.25, color: F([1, 1, 1]) }),
    chromAb: 0.1, grain: 0,
  }),

  neon: withV10({
    name: 'Neon', description: 'Неоновые контуры',
    ssao: F({ enabled: false }),
    bloom: F({ enabled: true, threshold: 0.25, softKnee: 0.5, intensity: 0.6, passes: 6 }),
    godRays: F({ enabled: true, intensity: 0.8, density: 1.0, decay: 0.96, weight: 0.5 }),
    fxaa: F({ enabled: true }), sharpen: 0.3,
    vignette: F({ intensity: 0.6, radius: 0.7 }),
    toneMap: F({ mode: 1, exposure: 1.6, gamma: 2.4 }),
    colorGrade: F({ saturation: 1.4, contrast: 1.3, tint: F([0.9, 0.95, 1.1]), temperature: -0.3 }),
    dof: F({ intensity: 0.15, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.7, color: F([0.2, 0.8, 1]) }),
    chromAb: 0.3, grain: 0.05,
  }, {
    volumetric: F({ enabled: true, intensity: 0.9, scattering: 0.55, absorption: 0.02,
                    anisotropy: 0.75, steps: 48, shadows: true, maxDistance: 45 }),
    motionBlur: F({ enabled: true, intensity: 0.4, samples: 16, shutterAngle: 0.6 }),
  }),

  retroCT: withV10({
    name: 'Ретро КТ', description: 'Стиль КТ-плёнки 90-х',
    ssao: F({ enabled: true, radius: 6, intensity: 0.3, bias: 0.02, samples: 12 }),
    bloom: F({ enabled: true, threshold: 0.7, softKnee: 0.4, intensity: 0.1, passes: 3 }),
    godRays: F({ enabled: false }), fxaa: F({ enabled: false }), sharpen: 0.2,
    vignette: F({ intensity: 0.55, radius: 0.7 }),
    toneMap: F({ mode: 3, exposure: 1.0, gamma: 2.4 }),
    colorGrade: F({ saturation: 0.6, contrast: 1.15, tint: F([1.05, 0.98, 0.88]), temperature: 0.4 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0, color: F([1, 1, 1]) }),
    chromAb: 0.05, grain: 0.35,
  }, {
    // Зерно здесь — стилистический приём, денойзить его нельзя
    denoise: F({ enabled: false, preset: 'off', strength: 0 }),
  }),

  mriFlair: withV10({
    name: 'МРТ FLAIR', description: 'Холодный контрастный МРТ',
    ssao: F({ enabled: true, radius: 10, intensity: 0.4, bias: 0.01, samples: 20 }),
    bloom: F({ enabled: false }), godRays: F({ enabled: false }),
    fxaa: F({ enabled: true }), sharpen: 0.6,
    vignette: F({ intensity: 0.15, radius: 0.9 }),
    toneMap: F({ mode: 1, exposure: 1.25, gamma: 2.0 }),
    colorGrade: F({ saturation: 0.7, contrast: 1.35, tint: F([0.92, 0.96, 1.08]), temperature: -0.6 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.2, color: F([0.5, 0.7, 1]) }),
    chromAb: 0, grain: 0,
  }, {
    denoise: F({ enabled: true, preset: 'strong', strength: 1.0 }),
  }),

  petGlow: withV10({
    name: 'ПЭТ Свечение', description: 'Горячий colormap для ПЭТ/КТ',
    ssao: F({ enabled: false }),
    bloom: F({ enabled: true, threshold: 0.2, softKnee: 0.5, intensity: 0.7, passes: 6 }),
    godRays: F({ enabled: true, intensity: 0.9, density: 1.0, decay: 0.97, weight: 0.5 }),
    fxaa: F({ enabled: true }), sharpen: 0.15,
    vignette: F({ intensity: 0.3, radius: 0.8 }),
    toneMap: F({ mode: 1, exposure: 1.8, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.5, contrast: 1.1, tint: F([1.15, 0.9, 0.7]), temperature: 0.8 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.3, color: F([1, 0.5, 0.1]) }),
    chromAb: 0.1, grain: 0,
  }, {
    volumetric: F({ enabled: true, intensity: 0.8, scattering: 0.5, absorption: 0.04,
                    anisotropy: 0.7, steps: 40, shadows: false, maxDistance: 40 }),
  }),

  cardiac: withV10({
    name: 'Кардио', description: 'Оптимизирован для сердца',
    ssao: F({ enabled: true, radius: 8, intensity: 0.5, bias: 0.01, samples: 20 }),
    bloom: F({ enabled: true, threshold: 0.55, softKnee: 0.3, intensity: 0.3, passes: 5 }),
    godRays: F({ enabled: true, intensity: 0.4, density: 0.85, decay: 0.95, weight: 0.3 }),
    fxaa: F({ enabled: true }), sharpen: 0.5,
    vignette: F({ intensity: 0.2, radius: 0.85 }),
    toneMap: F({ mode: 1, exposure: 1.4, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.05, contrast: 1.1, tint: F([1.02, 0.96, 0.92]), temperature: 0.15 }),
    dof: F({ intensity: 0.2, focus: 0.45, range: 0.35 }),
    edge: F({ intensity: 0.12, color: F([0.8, 0.3, 0.3]) }),
    chromAb: 0.05, grain: 0.08,
  }, {
    // Сердце в кино-режиме — единственный случай, где motion blur несёт
    // диагностический смысл: подчёркивает фазу сокращения
    motionBlur: F({ enabled: true, intensity: 0.25, samples: 12, shutterAngle: 0.4 }),
    sss: F({ enabled: true, strength: 0.4, radius: 1.1,
             falloffR: 1.0, falloffG: 3.5, falloffB: 7.0,
             thicknessScale: 2.2, samples: 12 }),
  }),

  neuro: withV10({
    name: 'Нейро', description: 'Нейрохирургический',
    ssao: F({ enabled: true, radius: 5, intensity: 0.7, bias: 0.01, samples: 24 }),
    bloom: F({ enabled: false }), godRays: F({ enabled: false }),
    fxaa: F({ enabled: true }), sharpen: 0.9,
    vignette: F({ intensity: 0.1, radius: 0.95 }),
    toneMap: F({ mode: 0, exposure: 1.3, gamma: 2.0 }),
    colorGrade: F({ saturation: 0.8, contrast: 1.25, tint: F([0.95, 0.98, 1.05]), temperature: -0.3 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.35, color: F([0.6, 0.8, 1]) }),
    chromAb: 0, grain: 0,
  }, {
    ssgi: F({ enabled: true, intensity: 0.35, radius: 18, bounces: 1,
              thickness: 0.05, samples: 20, temporal: true }),
  }),

  dental: withV10({
    name: 'Стоматология', description: 'Высокий контраст костей',
    ssao: F({ enabled: true, radius: 4, intensity: 0.6, bias: 0.02, samples: 16 }),
    bloom: F({ enabled: true, threshold: 0.8, softKnee: 0.2, intensity: 0.1, passes: 3 }),
    godRays: F({ enabled: false }), fxaa: F({ enabled: true }), sharpen: 1.0,
    vignette: F({ intensity: 0.3, radius: 0.8 }),
    toneMap: F({ mode: 1, exposure: 1.5, gamma: 2.0 }),
    colorGrade: F({ saturation: 0.7, contrast: 1.4, tint: F([1.05, 1.0, 0.92]), temperature: 0.1 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.4, color: F([1, 0.9, 0.7]) }),
    chromAb: 0, grain: 0,
  }),

  chest: withV10({
    name: 'Грудная клетка', description: 'Лёгочные структуры',
    ssao: F({ enabled: true, radius: 10, intensity: 0.4, bias: 0.01, samples: 20 }),
    bloom: F({ enabled: true, threshold: 0.6, softKnee: 0.3, intensity: 0.15, passes: 4 }),
    godRays: F({ enabled: false }), fxaa: F({ enabled: true }), sharpen: 0.4,
    vignette: F({ intensity: 0.2, radius: 0.85 }),
    toneMap: F({ mode: 1, exposure: 1.35, gamma: 2.2 }),
    colorGrade: F({ saturation: 0.95, contrast: 1.15, tint: F([0.98, 1.0, 1.02]), temperature: -0.1 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.08, color: F([0.5, 0.7, 0.9]) }),
    chromAb: 0, grain: 0.05,
  }, {
    // Лёгкие — воздушная среда: объёмное рассеяние тут физически уместно
    volumetric: F({ enabled: true, intensity: 0.35, scattering: 0.25, absorption: 0.08,
                    anisotropy: 0.3, steps: 32, shadows: true, maxDistance: 35 }),
  }),

  angio: withV10({
    name: 'Ангиография', description: 'Сосудистое свечение',
    ssao: F({ enabled: false }),
    bloom: F({ enabled: true, threshold: 0.3, softKnee: 0.4, intensity: 0.5, passes: 6 }),
    godRays: F({ enabled: true, intensity: 0.7, density: 0.95, decay: 0.96, weight: 0.4 }),
    fxaa: F({ enabled: true }), sharpen: 0.35,
    vignette: F({ intensity: 0.4, radius: 0.75 }),
    toneMap: F({ mode: 1, exposure: 1.6, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.1, contrast: 1.2, tint: F([0.9, 0.95, 1.1]), temperature: -0.2 }),
    dof: F({ intensity: 0.15, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.5, color: F([0.3, 0.6, 1]) }),
    chromAb: 0.15, grain: 0,
  }, {
    volumetric: F({ enabled: true, intensity: 0.6, scattering: 0.4, absorption: 0.03,
                    anisotropy: 0.7, steps: 44, shadows: false, maxDistance: 45 }),
  }),

  emergency: withV10({
    name: 'Экстренный', description: 'Максимальная читаемость',
    ssao: F({ enabled: false }), bloom: F({ enabled: false }),
    godRays: F({ enabled: false }), fxaa: F({ enabled: true }), sharpen: 0.5,
    vignette: F({ intensity: 0 }),
    toneMap: F({ mode: 0, exposure: 1.6, gamma: 1.8 }),
    colorGrade: F({ saturation: 1.0, contrast: 1.3, tint: F([1, 1, 1]), temperature: 0 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.15, color: F([1, 1, 1]) }),
    chromAb: 0, grain: 0,
  }, {
    // Всё дорогое выключено намеренно: в экстренном режиме важнее
    // мгновенный отклик, чем красота кадра
    denoise: F({ enabled: true, preset: 'light', strength: 0.35 }),
    upscale: F({ enabled: true, mode: 'fsr', quality: 'performance', sharpness: 0.35 }),
  }),

  teaching: withV10({
    name: 'Учебный', description: 'Все структуры подчёркнуты',
    ssao: F({ enabled: true, radius: 12, intensity: 0.6, bias: 0.01, samples: 24 }),
    bloom: F({ enabled: true, threshold: 0.5, softKnee: 0.3, intensity: 0.2, passes: 4 }),
    godRays: F({ enabled: true, intensity: 0.3, density: 0.8, decay: 0.94, weight: 0.25 }),
    fxaa: F({ enabled: true }), sharpen: 0.6,
    vignette: F({ intensity: 0.15, radius: 0.9 }),
    toneMap: F({ mode: 1, exposure: 1.4, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.2, contrast: 1.15, tint: F([1, 0.98, 0.96]), temperature: 0.1 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.3, color: F([0.5, 0.8, 0.5]) }),
    chromAb: 0.05, grain: 0,
  }, {
    ssgi: F({ enabled: true, intensity: 0.5, radius: 26, bounces: 2,
              thickness: 0.06, samples: 16, temporal: true }),
    sss: F({ enabled: true, strength: 0.35, radius: 1.0,
             falloffR: 1.0, falloffG: 3.5, falloffB: 7.0,
             thicknessScale: 2.0, samples: 10 }),
  }),

  night: withV10({
    name: 'Ночной', description: 'Для дежурств',
    ssao: F({ enabled: true, radius: 8, intensity: 0.3, bias: 0.01, samples: 16 }),
    bloom: F({ enabled: true, threshold: 0.7, softKnee: 0.3, intensity: 0.1, passes: 3 }),
    godRays: F({ enabled: false }), fxaa: F({ enabled: true }), sharpen: 0.3,
    vignette: F({ intensity: 0.4, radius: 0.8 }),
    toneMap: F({ mode: 1, exposure: 0.8, gamma: 2.4 }),
    colorGrade: F({ saturation: 0.85, contrast: 0.95, tint: F([1.08, 0.95, 0.78]), temperature: 0.7 }),
    dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
    edge: F({ intensity: 0.05, color: F([1, 0.8, 0.5]) }),
    chromAb: 0, grain: 0.1,
  }, {
    denoise: F({ enabled: true, preset: 'strong', strength: 0.9 }),
  }),

  presentation: withV10({
    name: 'Презентация', description: 'Максимальный вау-эффект',
    ssao: F({ enabled: true, radius: 10, intensity: 0.7, bias: 0.01, samples: 28 }),
    bloom: F({ enabled: true, threshold: 0.4, softKnee: 0.4, intensity: 0.4, passes: 6 }),
    godRays: F({ enabled: true, intensity: 0.6, density: 0.9, decay: 0.96, weight: 0.4 }),
    fxaa: F({ enabled: true }), sharpen: 0.5,
    vignette: F({ intensity: 0.5, radius: 0.75 }),
    toneMap: F({ mode: 1, exposure: 1.5, gamma: 2.2 }),
    colorGrade: F({ saturation: 1.3, contrast: 1.2, tint: F([1.0, 0.97, 0.93]), temperature: 0.15 }),
    dof: F({ intensity: 0.25, focus: 0.5, range: 0.35 }),
    edge: F({ intensity: 0.2, color: F([0.4, 0.7, 1]) }),
    chromAb: 0.15, grain: 0.1,
  }, {
    volumetric: F({ enabled: true, intensity: 0.7, scattering: 0.4, absorption: 0.04,
                    anisotropy: 0.7, steps: 56, shadows: true, maxDistance: 50 }),
    ssgi: F({ enabled: true, intensity: 0.5, radius: 30, bounces: 3,
              thickness: 0.07, samples: 24, temporal: true }),
    motionBlur: F({ enabled: true, intensity: 0.35, samples: 16, shutterAngle: 0.5 }),
    bokeh: F({ enabled: true, size: 0.7, bright: 2.5, rings: 5 }),
    sss: F({ enabled: true, strength: 0.45, radius: 1.3,
             falloffR: 1.0, falloffG: 3.5, falloffB: 7.0,
             thicknessScale: 2.0, samples: 14 }),
  }),
};

// ═══════════════════════════════════════════════════════════════════
// НОВЫЕ ПРЕСЕТЫ v10
// ═══════════════════════════════════════════════════════════════════

_PRESETS.volumetricCinema = withV10({
  name: 'Объёмное кино', description: 'v10 — акцент на объёмном свете',
  ssao: F({ enabled: true, radius: 9, intensity: 0.55, bias: 0.01, samples: 24 }),
  bloom: F({ enabled: true, threshold: 0.55, softKnee: 0.35, intensity: 0.3, passes: 5 }),
  godRays: F({ enabled: false }), fxaa: F({ enabled: true }), sharpen: 0.35,
  vignette: F({ intensity: 0.45, radius: 0.8 }),
  toneMap: F({ mode: 3, exposure: 1.35, gamma: 2.2 }),
  colorGrade: F({ saturation: 1.15, contrast: 1.1, tint: F([0.98, 0.99, 1.04]), temperature: -0.1 }),
  dof: F({ intensity: 0.3, focus: 0.5, range: 0.35 }),
  edge: F({ intensity: 0.1, color: F([0.5, 0.7, 1]) }),
  chromAb: 0.12, grain: 0.15,
}, {
  volumetric: F({ enabled: true, intensity: 1.0, scattering: 0.55, absorption: 0.06,
                  anisotropy: 0.78, steps: 64, shadows: true, maxDistance: 60 }),
  ssgi: F({ enabled: true, intensity: 0.55, radius: 32, bounces: 3,
            thickness: 0.08, samples: 24, temporal: true }),
  motionBlur: F({ enabled: true, intensity: 0.3, samples: 16, shutterAngle: 0.5 }),
  bokeh: F({ enabled: true, size: 0.6, bright: 2.2, rings: 5 }),
  denoise: F({ enabled: true, preset: 'balanced', strength: 0.75 }),
});

_PRESETS.performance = withV10({
  name: 'Производительность', description: 'v10 — апскейл и минимум проходов',
  ssao: F({ enabled: true, radius: 6, intensity: 0.45, bias: 0.015, samples: 12 }),
  bloom: F({ enabled: true, threshold: 0.7, softKnee: 0.25, intensity: 0.18, passes: 3 }),
  godRays: F({ enabled: false }), fxaa: F({ enabled: true }), sharpen: 0.45,
  vignette: F({ intensity: 0.25, radius: 0.88 }),
  toneMap: F({ mode: 1, exposure: 1.35, gamma: 2.2 }),
  colorGrade: F({ saturation: 1.05, contrast: 1.1, tint: F([1, 1, 1]), temperature: 0 }),
  dof: F({ intensity: 0, focus: 0.5, range: 0.3 }),
  edge: F({ intensity: 0.1, color: F([0.5, 0.7, 1]) }),
  chromAb: 0, grain: 0,
}, {
  // Рендер в 1/4 пикселей с последующим FSR — примерно те самые «2-3×»
  // из таблицы сравнения версий, и именно отсюда они берутся
  upscale: F({ enabled: true, mode: 'fsr', quality: 'performance', sharpness: 0.35 }),
  denoise: F({ enabled: true, preset: 'light', strength: 0.45 }),
});

export const CINEMATIC_PRESETS = Object.freeze(_PRESETS);
export const V10_PRESET_DEFAULTS = V10_DEFAULTS;

export const PRESET_IDS = Object.freeze(Object.keys(_PRESETS));

export const LUT_MODES = Object.freeze([
  { id: 0, name: 'Нет' },
  { id: 1, name: 'Kodak 2383' },
  { id: 2, name: 'Медицинский холодный' },
  { id: 3, name: 'Тёплый янтарь' },
  { id: 4, name: 'Teal & Orange' },
  { id: 5, name: 'Bleach Bypass' },
]);

export const TONE_MAP_MODES = Object.freeze([
  { id: 0, name: 'Linear' },
  { id: 1, name: 'ACES Filmic' },
  { id: 2, name: 'Reinhard' },
  { id: 3, name: 'Filmic U2' },
]);
